import type { ComputedRef, Ref } from 'vue'

import { serverErrorMessage } from '@/shared/api'

import { createInjectedState } from './injectedState'

/** A list that could not be read, and the way to ask for it again. */
export type ListError = {
  message: string
  retry: () => void
}

const { provideState, useState } = createInjectedState<ListError | null>(
  'shared-list-error',
  null
)

/** Not in the barrel: a slice announcing by hand re-derives the sentence and the retry. */
export const provideListError = provideState

/**
 * Announce to the table rendered inside that its query was refused, so it draws the server's
 * reason — or `fallback` when it gave none — and a "Try again" that refetches, instead of an
 * empty state.
 *
 * A list hook is where this belongs — `useEntityTable` and `useEntitySelect` already do it, so
 * a widget over them needs none of its own. Provided rather than passed as a prop because the
 * table that shows it sits several dumb components below whoever made the request.
 */
export function provideListErrorOf(
  query: {
    isError: Readonly<Ref<boolean>>
    error: Readonly<Ref<unknown>>
    refetch: () => unknown
  },
  fallback = 'Could not load the list.'
) {
  provideListError(() =>
    query.isError.value
      ? {
          message: serverErrorMessage(query.error.value, fallback),
          retry: () => void query.refetch()
        }
      : null
  )
}

/**
 * Why the nearest enclosing list is empty, or `null` when it is simply empty. A table rendered
 * outside a list hook — a form's own rows, a static list — injects nothing and reads `null`.
 */
export const useListError: () => ComputedRef<ListError | null> = useState
